import { PropsWithChildren, FC, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getSlideSourceType } from '@Admin/AdminQuotingPage/ImDfmSlidesPanel/logic'
import InteractionControlBox from '@Admin/AdminQuotingPage/ModelViewerPanel/InteractionControlBox'
import { DFM_SOURCE_TYPE_3D } from '@Admin/AdminQuotingPage/constants'

import Box from '~/core/Box'
import ModelViewer, { ModelViewerProps } from '~/core/ModelViewer'
import {
    getMeasurementUnits,
    getModelViewerConfig,
    getSelectedPatchHashes,
    getViewerInstance,
} from '~/redux-modules/qmr-viewer/selectors'
import {
    clickAnnotation,
    clickCrossSectionTool,
    clickDfmCard,
    createViewerInstance,
    escKeyUp,
    hoverCrossSectionTool,
    hoverPatch,
    hoverPoint,
    multiplePatchClick,
    multiplePatchHover,
    panCamera,
    patchClick,
    patchCtrlClick,
    patchRightClick,
    pointLeftClick,
    resetView,
    resetViewerState,
    rotateCamera,
    setMeasurementUnits,
    setTransparency,
    zoomCamera,
} from '~/redux-modules/qmr-viewer/actions'
import { ViewerStateType } from '~/redux-modules/qmr-viewer/types'
import InfoBar from '~/apps/consumer/components/BuilderModelViewer/InfoBar'
import MeasurePanel from '~/apps/consumer/components/MeasurePanel'
import { getNewCameraConfig } from '~/apps/consumer/components/BuilderModelViewer/logic'

import styles from './styles.css'

interface ImDfm3dViewerPropType {
    dfmFeedback?: any
    holes?: any
    isLocatedInModal?: any
    sceneBundle?: any
    selectedSlideDfmId?: any
}

const ImDfm3dViewer: FC<PropsWithChildren<ImDfm3dViewerPropType>> = ({
    dfmFeedback,
    holes = [],
    isLocatedInModal,
    sceneBundle,
    selectedSlideDfmId,
}) => {
    const dispatch = useDispatch()

    const [isResetView, setResetView] = useState(false)

    const selectedPatchHashes = useSelector(getSelectedPatchHashes)
    const units = useSelector(getMeasurementUnits)
    const viewer = useSelector(getViewerInstance)

    const {
        annotations: annotationConfigs,
        cameraConfig,
        crossSectionConfig,
        customP2PPoints,
        dfmFeatureConfigs,
        hoveredPoint,
        interactionMode,
        partRotation,
        patchConfigs,
        selectedPoints,
        transparent,
    } = useSelector((state: Record<string, ViewerStateType>) =>
        getModelViewerConfig(state, {
            dfmFeedbackArr: dfmFeedback,
            holes,
        }),
    )

    const onPatchHover: ModelViewerProps['onPatchHover'] = ({ patchHash }) => {
        dispatch(hoverPatch(patchHash))
    }

    const onCrossSectionToolClick: ModelViewerProps['onCrossSectionToolClick'] =
        ({ normal, pointCoordinates }) => {
            dispatch(clickCrossSectionTool({ normal, pointCoordinates }))
        }

    const onCrossSectionToolHover: ModelViewerProps['onCrossSectionToolHover'] =
        ({ tool = null }) => {
            if (tool) {
                dispatch(hoverCrossSectionTool(tool.name))
            }

            return null
        }

    const onPatchClick: ModelViewerProps['onPatchClick'] = patchClickEvent => {
        dispatch(patchClick(patchClickEvent))
    }

    const onPatchCtrlClick: ModelViewerProps['onPatchCtrlClick'] =
        patchClickEvent => {
            dispatch(patchCtrlClick(patchClickEvent))
        }

    const onPatchRightClick: ModelViewerProps['onPatchRightClick'] =
        patchClickEvent => {
            dispatch(patchRightClick(patchClickEvent))
        }

    const onAnnotationClick: ModelViewerProps['onAnnotationClick'] = ({
        annotation,
    }) => {
        dispatch(clickAnnotation({ annotation }))
    }

    const onEscKeyUp = () => {
        dispatch(escKeyUp())
    }

    const onCameraZoom = () => {
        const newCameraConfig = getNewCameraConfig({ viewer, cameraConfig })

        dispatch(zoomCamera(newCameraConfig))
    }

    const onCameraPan = () => {
        const newCameraConfig = getNewCameraConfig({ viewer, cameraConfig })

        dispatch(panCamera(newCameraConfig))
    }

    const onCameraRotation = () => {
        const newCameraConfig = getNewCameraConfig({ viewer, cameraConfig })

        dispatch(rotateCamera(newCameraConfig))
    }

    const onViewCubeClick = () => {
        const newCameraConfig = getNewCameraConfig({ viewer, cameraConfig })

        dispatch(rotateCamera(newCameraConfig))
    }

    const onViewerInstantiate = (viewerInstance: any) => {
        dispatch(createViewerInstance({ viewer: viewerInstance }))
    }

    const onPointHover: ModelViewerProps['onPointHover'] = point => {
        // Let's avoid dispatching if we don't need to as the re-rendering of the ModelViewer component
        // is kind of inefficient at the moment
        if (point?.index !== hoveredPoint?.index) {
            dispatch(hoverPoint(point?.index, point?.type))
        }
    }

    const onPointLeftClick: ModelViewerProps['onPointLeftClick'] = ({
        clickIndex,
        clickType,
    }) => {
        dispatch(pointLeftClick(clickIndex, clickType))
    }

    const onMultiplePatchHover: ModelViewerProps['onMultiplePatchHover'] = ({
        patchHashes,
    }) => {
        dispatch(multiplePatchHover(patchHashes))
    }

    const onMultiplePatchClick: ModelViewerProps['onMultiplePatchClick'] = ({
        patchHashes,
    }) => {
        dispatch(multiplePatchClick(patchHashes))
    }

    const sourceType = getSlideSourceType(dfmFeedback, selectedSlideDfmId)

    const onResetView = () => {
        const dfmSnapshot = dfmFeedback?.find(
            (dfm: any) => dfm.id === selectedSlideDfmId,
        )?.modelViewerSnapshots[0]?.snapshot

        if (sourceType == DFM_SOURCE_TYPE_3D && dfmSnapshot) {
            dispatch(
                clickDfmCard({
                    dfmId: selectedSlideDfmId,
                    dfmSnapshot,
                }),
            )
            setResetView(true)
        } else {
            dispatch(resetView())
        }
    }

    const onSelectMeasurementUnits = (unitsToSet: any) => {
        dispatch(setMeasurementUnits(unitsToSet))
    }

    const onToggleTransparent = () => {
        dispatch(setTransparency(!transparent))
    }

    const onUnmount = () => {
        return dispatch(resetViewerState())
    }

    const measurements = []

    if (viewer) {
        const measurementInfo = viewer.getMeasurement({
            patchHashes: selectedPatchHashes,
            points: selectedPoints,
            units,
        })

        if (measurementInfo) {
            measurements.push(measurementInfo)
        }
    }

    const measurementPanel = {
        units,
        measures: measurements,
    }

    const modelViewerProps: ModelViewerProps = {
        annotationConfigs,
        cameraConfig,
        crossSectionConfig,
        customP2PPoints,
        dfmFeatureConfigs,
        hoveredPoint,
        interactionMode,
        isResetView,
        onAnnotationClick,
        onCameraPan,
        onCameraRotation,
        onCameraZoom,
        onCrossSectionToolClick,
        onCrossSectionToolHover,
        onEscKeyUp,
        onMultiplePatchClick,
        onMultiplePatchHover,
        onPatchClick,
        onPatchCtrlClick,
        onPatchHover,
        onPatchRightClick,
        onPointHover,
        onPointLeftClick,
        onUnmount,
        onViewCubeClick,
        onViewerInstantiate,
        partRotation,
        patchConfigs,
        sceneBundle,
        selectedPoints,
        setResetView,
        transparent,
    }

    return (
        <Box
            id="im-dfm-3d-viewer"
            className={
                isLocatedInModal
                    ? styles.viewerToolsModalWrapper
                    : styles.viewerToolsWrapper
            }
        >
            <Box className={styles.viewerWrapper}>
                <ModelViewer {...modelViewerProps} />
                <InteractionControlBox />
                <InfoBar
                    onReset={onResetView}
                    onToggleTransparent={onToggleTransparent}
                    sourceType={sourceType}
                    transparentMode={transparent}
                />
                <MeasurePanel
                    onSelectMeasurementUnits={onSelectMeasurementUnits}
                    panel={{}}
                    values={measurementPanel}
                />
            </Box>
        </Box>
    )
}

export default ImDfm3dViewer
